import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import * as Yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { API } from 'api';
import { enqueueSnackbar } from 'notistack';
import {
  logUnexpectedErrorAndNotifyUser,
  logUnexpectedErrorAndNotifyUserWithTryAgain,
} from 'shared/handleUnexpectedError';
import { HttpStatusCode } from 'axios';
import { LogoWithTagline } from 'components/LogoWithTagline';

type FormValues = {
  newPassword1: string;
  newPassword2: string;
};

const initialValues: FormValues = {
  newPassword1: '',
  newPassword2: '',
};

export const SetPasswordForm = (): JSX.Element => {
  const history = useHistory();
  const { search } = useLocation();
  const { t } = useTranslation();

  const validationSchema = Yup.object({
    newPassword1: Yup.string()
      .required(t('setPassword.inputs.newPassword1.required'))
      .min(3, t('setPassword.inputs.newPassword1.error')),
    newPassword2: Yup.string()
      .required(t('setPassword.inputs.newPassword1.required'))
      .min(3, t('setPassword.inputs.newPassword2.error')),
  });

  const { control, handleSubmit, setError } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const { isPending, mutate } = useMutation({
    mutationFn: (newPasswordForm: {
      newPassword1: string;
      newPassword2: string;
    }) => {
      const uid = new URLSearchParams(search).get('uid');
      const token = new URLSearchParams(search).get('token');

      return API.post('auth/password/reset/confirm/', {
        ...newPasswordForm,
        uid,
        token,
      });
    },
  });

  const handleResetPassword: SubmitHandler<FormValues> = (values) => {
    mutate(values, {
      onSuccess: () => {
        enqueueSnackbar(t('setPassword.successMessage'), {
          variant: 'success',
        });
        history.push(`/login`);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        const statusCode = error.response?.status;

        if (statusCode === HttpStatusCode.Forbidden) {
          enqueueSnackbar(t('resetPassword.linkExpired'), { variant: 'error' });
          history.push('/reset_password');
        } else if (statusCode !== HttpStatusCode.BadRequest) {
          logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
          return;
        }

        const errorData = error.response.data;

        if (errorData.newPassword1) {
          setError('newPassword1', {
            type: 'custom',
            message: errorData.newPassword1.join('\n'),
          });
        }

        if (errorData.newPassword2) {
          setError('newPassword2', {
            type: 'custom',
            message: errorData.newPassword2.join('\n'),
          });
        }

        if (errorData.uid || errorData.token) {
          // Generic error message
          logUnexpectedErrorAndNotifyUser(t, { error });
        }
      },
    });
  };

  return (
    <form
      autoComplete="off"
      onSubmit={handleSubmit(handleResetPassword)}
      noValidate
    >
      <Stack spacing={6}>
        <LogoWithTagline />

        <Stack spacing={4}>
          <Stack spacing={2}>
            <Typography component="h2" variant="h3">
              {t('setPassword.subtitle')}
            </Typography>

            <Controller
              name="newPassword1"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const { ref, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="field-control-newPassword1"
                    label={t('setPassword.inputs.newPassword1.label')}
                    fullWidth
                    type="password"
                    {...muiFieldProps}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    required
                  />
                );
              }}
            />

            <Controller
              name="newPassword2"
              control={control}
              render={({ field, fieldState }) => {
                const { ref, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="field-control-newPassword2"
                    label={t('setPassword.inputs.newPassword2.label')}
                    fullWidth
                    type="password"
                    {...muiFieldProps}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    required
                  />
                );
              }}
            />
          </Stack>

          <Button disabled={isPending} type="submit" fullWidth size="large">
            {isPending ? (
              <CircularProgress size={24} color="secondary" />
            ) : (
              t('setPassword.button')
            )}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
